import { api } from '../api';

import type {
  CardDataType,
  SearchResultCardType,
  UpdateCardsType,
} from 'api/api.types';

export type CreateCardPayload = {
  data: CardDataType;
  viewOrder: number;
  boardId: number;
  author: number;
  reviewerId?: number;
  templateName: string;
  allAccess?: boolean;
  visibilityGroups?: number[];
  isDraft?: boolean;
  tags?: number[];
};

const injectedApi = api.injectEndpoints({
  endpoints: (build) => ({
    updateCard: build.mutation<any, UpdateCardsType>({
      query: (card) => ({
        url: `/cards.json?v=2`,
        method: 'PUT',
        body: card,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: any) => {
        return response;
      },
    }),
    createCard: build.mutation<any, CreateCardPayload>({
      query: (card) => ({
        url: `/cards.json?v=2`,
        method: 'POST',
        body: card,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: any) => {
        return response;
      },
    }),
    deleteCard: build.mutation<void, number | number[]>({
      query: (cardId) => ({
        url: Array.isArray(cardId) ? `/cards.json` : `/cards/${cardId}.json`,
        method: 'DELETE',
        body: Array.isArray(cardId) ? { cardIds: cardId } : undefined,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: (_result, _error, cardId) => {
        const ids = Array.isArray(cardId) ? cardId : [cardId];
        return ids.map((id) => ({ type: 'CardSchedule' as const, id }));
      },
      transformResponse: (response: any) => {
        return response;
      },
    }),
    searchCards: build.query<
      { items: Array<{ cardId: string; title: string; description?: string }> },
      {
        query: string;
        profileId?: number;
        size?: number;
        sort?: 'updated' | 'created' | 'name';
      }
    >({
      query: ({ query, profileId, size, sort }) => {
        const params: Record<string, string> = {
          index: 'cards',
          query,
        };

        if (profileId) {
          params.profileId = profileId.toString();
        }

        if (size) {
          params.size = size.toString();
        }

        if (sort) {
          params.sort = sort;
        }

        return {
          url: `/search.json`,
          method: 'GET',
          params,
        };
      },
      transformResponse: (response: {
        results: { results: SearchResultCardType[] };
      }) => {
        const items = (response?.results?.results || []).map((card) => ({
          cardId: card.id.toString(),
          title: card.data.name || 'Untitled Card',
          description: `${card.profile?.name} > ${card.board?.name}`,
        }));

        return { items };
      },
    }),
  }),
});

export const enhancedApi = injectedApi.enhanceEndpoints({
  addTagTypes: ['Cards'],
});

export const {
  useUpdateCardMutation,
  useCreateCardMutation,
  useDeleteCardMutation,
  useSearchCardsQuery,
  useLazySearchCardsQuery,
} = enhancedApi;
